/***
 *
 *   SUB NAV
 *   Secondary navigation for a section. Two shapes:
 *
 *     variant="tabs" (default) — the reference's underline `TabsList`
 *       (ui-reference/src/components/ui/tabs.tsx), used as a horizontal rail
 *       under the page header. This is what the ticket-category layout wants.
 *
 *     variant="rail" — the reference's vertical `SettingsNav`
 *       (ui-reference/src/components/settings/settings-nav.tsx). Phase 8 of
 *       docs/ui-refresh/PLAN.md switches the settings nav onto it; it is built
 *       here because that is where sub.scss died.
 *
 *   PROPS
 *   items: array of { link, label, icon, permission, exact, alert }
 *   variant: tabs | rail (default: tabs)
 *
 **********/

import { useContext } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { AuthContext } from 'app/auth';
import { Icon, cn } from 'components/lib';
import Style from './sub.tailwind.js';

export function SubNav(props) {
    const context = useContext(AuthContext);
    const location = useLocation();
    const variant = props.variant === 'rail' ? 'rail' : 'tabs';

    const item = (isActive) =>
        cn(
            Style[variant].item,
            isActive ? Style[variant].itemActive : Style[variant].itemIdle
        );

    return (
        <nav className={cn(Style[variant].nav, props.className)}>
            {props.items?.map((entry) => {
                if (entry.permission && !context.permission[entry.permission]) return null;

                const content = (
                    <>
                        {entry.icon && (
                            <Icon image={entry.icon} size={16} color="currentColor" className="shrink-0" />
                        )}
                        <span className="truncate">{entry.label}</span>
                        {entry.alert && (
                            <Icon
                                image="alert-circle"
                                color="hsl(var(--error))"
                                size={14}
                                className={Style.alert}
                            />
                        )}
                    </>
                );

                // `exact` items opt out of NavLink's nested matching by not being
                // a NavLink at all once the route has moved on to a child.
                if (entry.exact && entry.link !== location.pathname)
                    return (
                        <Link key={entry.label} to={entry.link} className={item(false)}>
                            {content}
                        </Link>
                    );

                return (
                    <NavLink
                        key={entry.label}
                        to={entry.link}
                        title={entry?.alert?.message}
                        className={({ isActive }) => item(isActive)}>
                        {content}
                    </NavLink>
                );
            })}
        </nav>
    );
}
